if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const primaryVariantId = formData.get('id');
        const itemQuantity = parseInt(formData.get('quantity') || 1, 10) || 1;
        const compulsoryVariants = this.getCompulsoryAddonVariants(primaryVariantId);
        const availableCompulsory = (Array.isArray(compulsoryVariants) ? compulsoryVariants : [])
          .filter((item) => item && item.id && item.available && String(item.id) !== String(primaryVariantId));
        const shouldAddCompulsory = availableCompulsory.length > 0;
        const requestFormData = shouldAddCompulsory
          ? this.buildItemsFormData(formData, primaryVariantId, itemQuantity, availableCompulsory)
          : formData;
        if (this.cart) {
          requestFormData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          requestFormData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = requestFormData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: primaryVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: primaryVariantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              const isNotification = this.cart && this.cart.tagName === 'CART-NOTIFICATION';
              const canRender = !!(this.cart && response && response.sections);
              const canRenderNotification = !isNotification || !!response.key;
              CartPerformance.measure("add:paint-updated-sections", () => {
                if (canRender && canRenderNotification) {
                  this.cart.renderContents(response);
                } else {
                  this.refreshCartSections();
                }
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      refreshCartSections() {
        if (!this.cart || !this.cart.getSectionsToRender) return;
        const sections = this.cart.getSectionsToRender();
        const sectionIds = sections.map((section) => section.id).join(',');
        const url = `${window.routes.cart_url}?sections=${encodeURIComponent(sectionIds)}`;

        fetch(url)
          .then((response) => response.json())
          .then((sectionsHtml) => {
            sections.forEach((section) => {
              const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
              if (!sectionElement) return;
              const html = sectionsHtml[section.id];
              sectionElement.innerHTML = this.getSectionInnerHTMLFromHTML(html, section.selector);
            });
            if (this.cart.tagName === 'CART-DRAWER' && typeof this.cart.open === 'function') {
              this.cart.open();
            }
          })
          .catch((e) => console.error(e));
      }

      getSectionInnerHTMLFromHTML(html, selector = '.shopify-section') {
        if (!html) return '';
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const element = doc.querySelector(selector);
        return element ? element.innerHTML : html;
      }

      getCompulsoryAddonVariants(primaryVariantId) {
        if (!primaryVariantId) return null;
        const map = this.getCompulsoryVariantMap();
        if (!map) return null;
        return map[primaryVariantId] || null;
      }

      getCompulsoryVariantMap() {
        const root = this.closest('product-info') || document;
        const mapEl = root.querySelector('variant-selects [data-compulsory-variant-map]');
        if (!mapEl || !mapEl.textContent) return null;
        try {
          return JSON.parse(mapEl.textContent);
        } catch (e) {
          console.warn('Invalid compulsory variant map', e);
          return null;
        }
      }

      buildItemsFormData(formData, primaryVariantId, quantity, addonVariants) {
        const itemsFormData = new FormData();
        itemsFormData.append('items[0][id]', primaryVariantId);
        itemsFormData.append('items[0][quantity]', quantity);
        (addonVariants || []).forEach((addon, index) => {
          if (!addon || !addon.id) return;
          const itemIndex = index + 1;
          itemsFormData.append(`items[${itemIndex}][id]`, addon.id);
          itemsFormData.append(`items[${itemIndex}][quantity]`, quantity);
        });

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) itemsFormData.append('items[0][selling_plan]', sellingPlan);

        for (const [key, value] of formData.entries()) {
          const propMatch = key.match(/^properties\[(.+)\]$/);
          if (!propMatch) continue;
          if (value === null || value === '') continue;
          itemsFormData.append(`items[0][properties][${propMatch[1]}]`, value);
        }

        return itemsFormData;
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
